import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Notification as AppNotification } from '@app/model/notification';
import { NotificationService } from '@app/services';
import { getNotificationRoute, getNotificationRule, NotificationRoute } from '../notification-rules';
import { AlertComponent } from '@app/shared/alert/alert.component';

// 為瀏覽器原生的 Notification API 創建類型別名

@Component({
  selector: 'app-notification-bar',
  standalone: true,
  imports: [CommonModule, AlertComponent],
  templateUrl: './notification-bar.component.html'
})
export class NotificationBarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Input() fontSize = '18px';
  @Input() isLogin = false;
  @Input() userId = '';

  @Output() close = new EventEmitter<void>();
  @Output() notificationsUpdated = new EventEmitter<AppNotification[]>();
  @Output() alertMessage = new EventEmitter<string>();

  personalNotifications: AppNotification[] = [];
  notificationMessage = '';
  showAlert = false;
  alertTimer: any;
  private updateInterval: any;
  isMonitoring = false; // 監聽狀態標記（改為公開以供模板使用）

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    // 只在用戶登入時才開始監聽通知
    if (this.isLogin && this.userId) {
      this.startNotificationMonitoring();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // 當登入狀態或用戶ID改變時，重新初始化通知監聽
    if ((changes['isLogin'] || changes['userId']) && this.isLogin && this.userId) {
      this.stopNotificationMonitoring();
      this.startNotificationMonitoring();
    } else if (changes['isLogin'] && !this.isLogin) {
      this.stopNotificationMonitoring();
      this.personalNotifications = [];
    }
    
    // 當 isOpen 狀態改變時，控制背景滾動
    if (changes['isOpen'] && isPlatformBrowser(this.platformId)) {
      if (changes['isOpen'].currentValue) {
        // 開啟時禁用背景滾動並重新 fetch 通知
        this.disableBodyScroll();
        if (this.isLogin && this.userId) {
          // console.log(' Notification Bar 打開，重新 fetch 通知');
          this.fetchNotifications();
        }
      } else {
        // 關閉時恢復背景滾動
        this.enableBodyScroll();
      }
    }
    
    // 當 isOpen 狀態改變為 true 時（打開通知欄），重新 fetch 通知（保留原有邏輯作為備份）
    if (changes['isOpen'] && changes['isOpen'].currentValue === true && this.isLogin && this.userId && !changes['isOpen'].previousValue) {
      // console.log(' Notification Bar 打開，重新 fetch 通知（備份邏輯）');
      this.fetchNotifications();
    }
  }

  ngOnDestroy() {
    this.stopNotificationMonitoring();
    if (this.isOpen) {
      this.enableBodyScroll();
    }
    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
    }
  }

  onClose() {
    this.close.emit();
  }

  onLoginPage() {
    this.navigateAndClose('login');
  }

  // 防止背景滾動的方法（包含手機平台優化）
  private disableBodyScroll() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
  }

  private enableBodyScroll() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }

  // 輔助方法：導航並關閉通知欄
  private navigateAndClose(route: string[] | string) {
    // console.log(' 通知欄導航到:', route, '並關閉通知欄');
    this.close.emit();
    if (typeof route === 'string') {
      this.router.navigate([route]);
    } else {
      this.router.navigate(route);
    }
  }

  private async markReadAndRefresh(notification: AppNotification) {
    await this.notificationService.readNotification(notification);
    await this.fetchNotifications();
  }

  private navigateRoute(route: NotificationRoute) {
    if (!route) return;

    this.close.emit();
    if (typeof route === 'string') {
      this.router.navigate([route]);
    } else {
      this.router.navigate(route);
    }
  }

  async onReadAllNotifications() {
    // console.log(' 標記所有通知為已讀');
    await this.notificationService.deleteAllNotification(this.userId);
    // 重新 fetch 通知而不是重新載入頁面
    await this.fetchNotifications();
    // 標記所有通知為已讀後關閉通知欄
    this.close.emit();
  }

  async onClickNotification(notification: AppNotification) {
    const route = getNotificationRoute(notification);

    await this.markReadAndRefresh(notification);

    if (route) {
      this.navigateRoute(route);
      return;
    }

    this.close.emit();
  }

  async fetchNotifications() {
    try {
      const res = await this.notificationService.getNotificationByUserId();
      if (res && res.data) {
        const allNotifications: AppNotification[] = res.data;
        const currentUnreadNotifications = allNotifications.filter(n => !n.is_read);
        const newNotifications: AppNotification[] = [];
        
        // 檢查是否有新的未讀通知（比較當前未讀通知和之前的通知列表）
        currentUnreadNotifications.forEach((notification: AppNotification) => {
          if (!this.personalNotifications.some(n => n.id === notification.id)) {
            newNotifications.push(notification);
          }
        });
        
        // 如果有新通知，只顯示數量的簡化alert
        if (newNotifications.length > 0) {
          const alertMessage = ` 您有 ${newNotifications.length} 則新通知`;
          this.showNotificationAlert(alertMessage);
          
          // 在控制台記錄新通知詳細信息
          // console.log(' 檢測到新通知:', {
          //   count: newNotifications.length,
          //   notifications: newNotifications.map(n => ({
          //     id: n.id,
          //     type: n.notification_type,
          //     title: n.title,
          //     description: n.description,
          //     time: new Date(n.created_at).toLocaleString()
          //   }))
          // });
          
          // 額外記錄：監聽統計
          // console.log(` 通知監聽統計 - 總未讀: ${currentUnreadNotifications.length}, 新增: ${newNotifications.length}`);
        } else {
          // 沒有新通知時的日誌（可選，用於調試）
          // console.log(` 通知檢查完成 - 無新通知 (總未讀: ${currentUnreadNotifications.length})`);
        }
        
        // 發出事件通知父組件（無論是否有新通知都要發出，以確保通知數量同步）
        this.notificationsUpdated.emit(currentUnreadNotifications);
        
        // 更新通知列表
        this.personalNotifications = currentUnreadNotifications;
        this.sortNotification();
      }
    } catch (error) {
      console.error(' 獲取通知失敗：', error);
      // 顯示錯誤提示alert
      this.showNotificationAlert(' 通知獲取失敗，請檢查網路連接');
    }
  }

  // 公開方法：手動刷新通知（供父組件調用）
  public async refreshNotifications() {
    if (this.isLogin && this.userId) {
      // console.log(' 手動刷新通知');
      await this.fetchNotifications();
    }
  }

  async sortNotification() {
    this.personalNotifications.sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }

  showNotificationAlert(message: string) {
    // 確保每次都能顯示新的alert，即使上一個還沒消失
    this.closeAlert(); // 先關閉之前的alert
    
    // 稍微延遲以確保UI更新
    setTimeout(() => {
      this.notificationMessage = message;
      this.showAlert = true;

      // 設置自動關閉計時器（4秒後自動關閉，簡化後時間縮短）
      this.alertTimer = setTimeout(() => {
        this.closeAlert();
      }, 4000);
      
      // 發出 alert 事件到父組件
      this.alertMessage.emit(message);
      
      // 記錄alert顯示
      // console.log(' Alert顯示:', message);
    }, 100);
  }

  closeAlert() {
    this.showAlert = false;
    this.notificationMessage = '';
    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
      this.alertTimer = null;
    }
  }

  sortNotifications() {
    this.personalNotifications.sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    this.notificationsUpdated.emit(this.personalNotifications);
  }

  getBackgroundColor(notificationType: string): string {
    // This method is kept for backward compatibility but not used in the new design
    switch (notificationType) {
      case 'task_created': return 'var(--app-color-notice-neutral)';
      case 'delete_task': return 'var(--app-color-notice-danger)';
      case 'task_expired': return 'var(--app-color-notice-danger)';
      case 'task_match_list_update': return 'var(--app-color-notice-warning)';
      case 'task_denied': return 'var(--app-color-notice-danger)';
      case 'task_accepted': return 'var(--app-color-notice-success)';
      case 'finish_task': return 'var(--app-color-notice-neutral)';
      case 'complete_task': return 'var(--app-color-notice-success)';
      case 'identity_apply': return 'var(--app-color-notice-neutral)';
      case 'apply_success': return 'var(--app-color-notice-success)';
      case 'apply_unsuccess': return 'var(--app-color-notice-danger)';
      default: return 'var(--app-color-surface)';
    }
  }

  getNotificationIcon(notificationType: string): string {
    return getNotificationRule(notificationType).icon;
  }

  // 時間格式化方法
  getFormattedTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return '剛剛';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}分鐘前`;
    } else if (diffInMinutes < 1440) { // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}小時前`;
    } else if (diffInMinutes < 10080) { // 7 days
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}天前`;
    } else {
      // 超過7天顯示具體日期
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  private startNotificationMonitoring() {
    if (this.isMonitoring) {
      // console.log('  通知監聽已在運行中');
      return;
    }
    
    // console.log(' 開始通知監聽服務');
    // console.log(` 用戶ID: ${this.userId}`);
    // console.log(' 監聽間隔: 每30秒檢查一次');
    // console.log(' 功能: 每檢測到新通知立即alert');
    
    this.isMonitoring = true;
    
    // 初始獲取通知
    this.fetchNotifications();
    
    // 設置定期檢查（改為30秒間隔，避免過度頻繁的API調用）
    this.updateInterval = setInterval(() => {
      // 只在頁面可見時檢查通知
      if (document.visibilityState === 'visible') {
        this.fetchNotifications();
      }
    }, 30000); // 每30秒檢查一次新通知
  }

  private stopNotificationMonitoring() {
    if (this.updateInterval) {
      // console.log('  停止通知監聽');
      clearInterval(this.updateInterval);
      this.updateInterval = null;
      this.isMonitoring = false;
    }
  }

  // 測試功能：模擬新通知到達，驗證alert功能
  simulateNewNotification() {
    const notificationTypes = ['task_created', 'task_accepted', 'complete_task', 'apply_success'];
    const randomType = notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
    
    const testNotification: AppNotification = {
      id: Date.now().toString(),
      title: `測試通知 - ${randomType}`,
      description: `這是一個模擬的${randomType}通知，用於測試alert功能。時間: ${new Date().toLocaleTimeString()}`,
      notification_type: randomType,
      is_read: false,
      created_at: new Date().toISOString(),
      sent_to: this.userId,
      bound_id: Math.random().toString()
    };

    // 添加到通知列表（模擬新通知到達）
    this.personalNotifications.unshift(testNotification);
    this.sortNotification();

    // 觸發簡化的alert（只顯示數量，與fetchNotifications保持一致）
    this.showNotificationAlert(` 您有 1 則新通知`);
    
    // console.log(' 模擬新通知已創建並觸發alert:', {
    //   id: testNotification.id,
    //   type: testNotification.notification_type,
    //   title: testNotification.title,
    //   time: new Date().toLocaleTimeString()
    // });
    
    // 模擬通知統計更新
    this.notificationsUpdated.emit(this.personalNotifications);
  }

  // 手機風格通知的背景class
  getNotificationBgClass(notificationType: string): string {
    return getNotificationRule(notificationType).bgClass;
  }

  // 通知類型文字
  getNotificationTypeText(notificationType: string): string {
    return getNotificationRule(notificationType).label;
  }

  // 通知狀態指示器class
  getNotificationStatusClass(notificationType: string): string {
    return getNotificationRule(notificationType).statusClass;
  }

  getNotificationActionText(notification: AppNotification): string {
    return getNotificationRoute(notification) ? '點擊查看詳情' : '點擊標記已讀';
  }

  parseFontSize(fontSize: string): number {
    return parseFloat(fontSize) || 20;
  }

  // 字體大小計算方法
  getHeaderFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 1.2}px`; // 標題比基礎字體大20%
  }

  getButtonFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 0.7}px`; // 按鈕文字比基礎字體小30%
  }

  getSmallFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 0.6}px`; // 小字體比基礎字體小40%
  }

  getTitleFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 0.8}px`; // 通知標題比基礎字體小20%
  }

  getDescriptionFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 0.7}px`; // 描述文字比基礎字體小30%
  }

  getEmptyStateTitleFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 0.9}px`; // 空狀態標題比基礎字體小10%
  }

  getEmptyStateDescFontSize(): string {
    const baseSize = parseFloat(this.fontSize);
    return `${baseSize * 0.7}px`; // 空狀態描述比基礎字體小30%
  }

}
